// ===============================================================
// file: companies.js
// ===============================================================
// UI for listing Companies from the root-level Firestore collection
//   • Anyone logged-in can **view / filter**
//   • Only admin UIDs can **add / edit / delete**
//
// Filters
// -------
//   • Full-text search (name / domain / industry)
//   • Country   (select)
//   • Industry  (select)
//   • Employees min / max
//   • Revenue   min / max
//
// Display
// -------
//   • All URLs clickable: Domain, LinkedIn Page, LinkedIn Sales
//   • createdAt timestamp shown
//   • lnkd_company_summary rendered with line-breaks preserved
//
// Form (admin)
// ------------
//   • name, domain, country, industry, size, employeesCount, revenueUsd,
//     LNKD-URL, LNKD-SalesURL, about, lnkd_company_summary, notes
//
// IMPORTANT – Keep ADMIN_UIDS in sync with Firestore rules.
// ===============================================================

import React, { useState, useEffect, useContext } from "react";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
} from "firebase/firestore";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSearch,
  FaSpinner,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { db } from "../firebase";
import { AuthContext } from "../AuthProvider";

/* ────────────────────────────────────────────────────────────
   GLOBAL ADMIN UIDs – must match Firestore rules isAdmin()
   ──────────────────────────────────────────────────────────── */
const ADMIN_UIDS = [
  "wVdZ4zMt1PYD6mmNflk6ZApiZEe2",
  "LRPoMCWtpFRT0tcK2bTi90HbVSZ2",
];

/* ────────────────────────────────────────────────────────────
   Helpers: number → K / M / B
   ──────────────────────────────────────────────────────────── */
const formatNumber = (n) => {
  if (n == null) return "";
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
};

/* ============================================================= */
/*                       MAIN COMPONENT                          */
/* ============================================================= */
const Companies = ({ teamId }) => {
  const { user } = useContext(AuthContext);
  const isAdmin = user && ADMIN_UIDS.includes(user.uid);

  /* ───────── State ───────── */
  const [companies, setCompanies] = useState([]);
  const [filtered, setFiltered] = useState([]);

  /* pagination */
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);

  /* search + filters */
  const [searchTerm, setSearch] = useState("");
  const [filterCountry, setFilterCountry] = useState("");
  const [filterIndustry, setFilterIndustry] = useState("");
  const [numFilter, setNumFilter] = useState({
    minEmp: "",
    maxEmp: "",
    minRev: "",
    maxRev: "",
  });

  const [loading, setLoading] = useState(true);

  /* modals */
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selected, setSelected] = useState(null);
  const [processing, setProcessing] = useState(false);

  /* form data */
  const blank = {
    name: "",
    domain: "",
    country: "",
    industry: "",
    email_pattern: "",
    size: "",
    employeesCount: "",
    revenueUsd: "",
    "LNKD-URL": "",
    "LNKD-SalesURL": "",
    about: "",
    lnkd_company_summary: "",
    notes: "",
  };
  const [form, setForm] = useState(blank);

  /* ───────── Fetch once ───────── */
  useEffect(() => {
    if (user && teamId) fetchCompanies();
  }, [user, teamId]);

  /* ───────── Build unique Country + Industry lists for selects ───────── */
  const countries = Array.from(
    new Set(companies.map((c) => c.Country).filter(Boolean))
  ).sort();
  const industries = Array.from(
    new Set(companies.map((c) => c.industry).filter(Boolean))
  ).sort();

  /* ───────── FILTERS ───────── */
  useEffect(() => {
    let rows = [...companies];

    /* full-text search */
    const term = searchTerm.toLowerCase();
    if (term)
      rows = rows.filter(
        (c) =>
          c.name?.toLowerCase().includes(term) ||
          c.domain?.toLowerCase().includes(term) ||
          c.industry?.toLowerCase().includes(term)
      );

    /* Country / Industry drop-downs */
    if (filterCountry) rows = rows.filter((c) => c.Country === filterCountry);
    if (filterIndustry)
      rows = rows.filter((c) => c.industry === filterIndustry);

    /* numeric ranges */
    const { minEmp, maxEmp, minRev, maxRev } = numFilter;
    const minE = parseInt(minEmp || 0, 10);
    const maxE = parseInt(maxEmp || Number.MAX_SAFE_INTEGER, 10);
    const minR = parseInt(minRev || 0, 10);
    const maxR = parseInt(maxRev || Number.MAX_SAFE_INTEGER, 10);
    rows = rows.filter((c) => {
      const e = c.employeesCount ?? Number.MAX_SAFE_INTEGER;
      const r = c.revenueUsd ?? Number.MAX_SAFE_INTEGER;
      return e >= minE && e <= maxE && r >= minR && r <= maxR;
    });

    setFiltered(rows);
  }, [searchTerm, filterCountry, filterIndustry, numFilter, companies]);

  /* ───────── Firestore read ───────── */
  const fetchCompanies = async () => {
    if (!user || !teamId) return;
    setLoading(true);
    try {
      // Try team collection first, fallback to root collection
      let snap = await getDocs(collection(db, "teams", teamId, "companies"));

      if (snap.empty) {
        console.log("No team companies found, falling back to root collection");
        snap = await getDocs(collection(db, "companies"));
        toast.info("Loading from root collection - migration recommended");
      }

      const rows = [];
      snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
      setCompanies(rows);
      setFiltered(rows);
      toast.success(`Loaded ${rows.length} companies`);
    } catch (e) {
      toast.error("Load failed");
    } finally {
      setLoading(false);
    }
  };

  /* ───────── Clear all filters ───────── */
  const clearFilters = () => {
    setSearch("");
    setFilterCountry("");
    setFilterIndustry("");
    setNumFilter({
      minEmp: "",
      maxEmp: "",
      minRev: "",
      maxRev: "",
    });
    setCurrentPage(1);
  };

  /* ───────── Pagination helpers ───────── */
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedCompanies = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const changeItemsPerPage = (newValue) => {
    setItemsPerPage(Number(newValue));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  /* ───────── Form helpers ───────── */
  const resetForm = () => setForm(blank);
  const populateForm = (c) =>
    setForm({
      ...blank,
      ...c,
      country: c.Country || "",
    });
  const handleChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  /* ───────── Add / Update ───────── */
  const saveCompany = async (e) => {
    e.preventDefault();
    if (!isAdmin) return toast.error("Admins only");
    if (!form.name || !form.domain)
      return toast.error("Name & domain required");

    setProcessing(true);
    try {
      if (selected) {
        await updateDoc(doc(db, "teams", teamId, "companies", selected.id), {
          ...form,
          lastUpdated: new Date(),
        });
        toast.success("Updated");
      } else {
        /* dup check */
        const ref = collection(db, "teams", teamId, "companies");
        const dup = await getDocs(
          query(ref, where("domain", "==", form.domain))
        );
        if (!dup.empty) throw new Error("duplicate");
        await addDoc(ref, {
          ...form,
          createdAt: new Date(),
          lastUpdated: new Date(),
        });
        toast.success("Added");
      }
      await fetchCompanies();
      setShowForm(false);
      setSelected(null);
      resetForm();
    } catch (err) {
      toast.error(
        err.message === "duplicate" ? "Domain exists" : "Save failed"
      );
    } finally {
      setProcessing(false);
    }
  };

  /* ───────── Delete ───────── */
  const delCompany = async (c) => {
    if (!isAdmin) return;
    if (!window.confirm("Delete this company?")) return;
    setProcessing(true);
    try {
      await deleteDoc(doc(db, "teams", teamId, "companies", c.id));
      toast.success("Deleted");
      await fetchCompanies();
    } catch {
      toast.error("Delete failed");
    } finally {
      setProcessing(false);
    }
  };

  /* ───────── View ───────── */
  const openView = (c) => {
    setSelected(c);
    setShowView(true);
  };

  /* ===========================================================
     RENDER
     =========================================================== */
  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 space-y-4 md:space-y-0">
        <h1 className="text-2xl font-bold">Companies</h1>
        {isAdmin && (
          <button
            onClick={() => {
              resetForm();
              setSelected(null);
              setShowForm(true);
            }}
            className="bg-purple-600 text-white px-4 py-2 rounded flex items-center hover:bg-purple-700"
          >
            <FaPlus className="mr-2" /> Add Company
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 lg:grid-cols-6 gap-4 mb-6">
        <div className="relative col-span-2">
          <input
            className="w-full p-3 pl-10 border rounded-md focus:ring-2 focus:ring-purple-500"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearch(e.target.value)}
          />
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        </div>

        <select
          value={filterCountry}
          onChange={(e) => setFilterCountry(e.target.value)}
          className="p-3 border rounded-md"
        >
          <option value="">All Countries</option>
          {countries.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>

        <select
          value={filterIndustry}
          onChange={(e) => setFilterIndustry(e.target.value)}
          className="p-3 border rounded-md"
        >
          <option value="">All Industries</option>
          {industries.map((i) => (
            <option key={i}>{i}</option>
          ))}
        </select>

        {["minEmp", "maxEmp", "minRev", "maxRev"].map((k, i) => (
          <input
            key={k}
            type="number"
            placeholder={
              i < 2
                ? i === 0
                  ? "Min Emp"
                  : "Max Emp"
                : i === 2
                ? "Min Rev $"
                : "Max Rev $"
            }
            value={numFilter[k]}
            onChange={(e) =>
              setNumFilter({ ...numFilter, [k]: e.target.value })
            }
            className="p-3 border rounded-md"
          />
        ))}
      </div>

      {/* Clear Filters Button */}
      <div className="mb-6">
        <button
          onClick={clearFilters}
          className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded"
        >
          Clear Filters
        </button>
      </div>

      {/* Items per page + Pagination */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6">
        <div className="flex items-center space-x-2 mb-4 md:mb-0">
          <span>Show</span>
          <select
            value={itemsPerPage}
            onChange={(e) => changeItemsPerPage(e.target.value)}
            className="border rounded px-2 py-1"
          >
            {[10, 25, 50, 100].map((num) => (
              <option key={num} value={num}>
                {num}
              </option>
            ))}
          </select>
          <span>per page</span>
        </div>

        <div className="flex items-center">
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-3 py-1 border rounded mr-1 ${
              currentPage === 1
                ? "bg-gray-100 text-gray-400"
                : "bg-white hover:bg-gray-100"
            }`}
          >
            Previous
          </button>

          <span className="mx-2">
            Page {currentPage} of {totalPages || 1}
          </span>

          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage >= totalPages}
            className={`px-3 py-1 border rounded ml-1 ${
              currentPage >= totalPages
                ? "bg-gray-100 text-gray-400"
                : "bg-white hover:bg-gray-100"
            }`}
          >
            Next
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-lg">
        {loading ? (
          <div className="p-8 text-center">
            <FaSpinner className="animate-spin text-2xl text-purple-600 mx-auto mb-2" />
            <p>Loading…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-600">No companies</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {[
                    "Name",
                    "Domain",
                    "Country",
                    "Industry",
                    "Email Pattern",
                    "Emp",
                    "Revenue",
                    "Created",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {paginatedCompanies.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">{c.name}</td>
                    <td className="px-4 py-3 text-blue-600">
                      <a
                        href={`https://${c.domain}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {c.domain}{" "}
                        <FaExternalLinkAlt className="inline text-xs" />
                      </a>
                    </td>
                    <td className="px-4 py-3">{c.Country || "—"}</td>
                    <td className="px-4 py-3">{c.industry || "—"}</td>
                    <td className="px-4 py-3">{c.email_pattern || "—"}</td>
                    <td className="px-4 py-3">
                      {c.employeesCount ? formatNumber(c.employeesCount) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      {c.revenueUsd ? `$${formatNumber(c.revenueUsd)}` : "—"}
                    </td>
                    <td className="px-4 py-3">
                      {c.createdAt?.toDate
                        ? c.createdAt.toDate().toLocaleDateString()
                        : new Date(c.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 space-x-2">
                      <button
                        className="text-blue-600"
                        onClick={() => openView(c)}
                      >
                        View
                      </button>
                      {isAdmin && (
                        <>
                          <button
                            className="text-indigo-600"
                            onClick={() => {
                              setSelected(c);
                              populateForm(c);
                              setShowForm(true);
                            }}
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="text-red-600"
                            onClick={() => delCompany(c)}
                          >
                            <FaTrash />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination (bottom) */}
      {filtered.length > 0 && (
        <div className="mt-4 flex justify-center">
          <div className="flex items-center">
            <button
              onClick={() => goToPage(1)}
              disabled={currentPage === 1}
              className={`px-3 py-1 border rounded mr-1 ${
                currentPage === 1
                  ? "bg-gray-100 text-gray-400"
                  : "bg-white hover:bg-gray-100"
              }`}
            >
              First
            </button>

            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-1 border rounded mr-1 ${
                currentPage === 1
                  ? "bg-gray-100 text-gray-400"
                  : "bg-white hover:bg-gray-100"
              }`}
            >
              Previous
            </button>

            <div className="mx-2 flex items-center">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                // Show 5 page numbers around current page
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else {
                  const leftOffset = Math.min(
                    Math.max(0, currentPage - 3),
                    Math.max(0, totalPages - 5)
                  );
                  pageNum = i + 1 + leftOffset;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => goToPage(pageNum)}
                    className={`w-8 h-8 mx-1 rounded-full ${
                      currentPage === pageNum
                        ? "bg-purple-600 text-white"
                        : "bg-white hover:bg-gray-100"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className={`px-3 py-1 border rounded ml-1 ${
                currentPage >= totalPages
                  ? "bg-gray-100 text-gray-400"
                  : "bg-white hover:bg-gray-100"
              }`}
            >
              Next
            </button>

            <button
              onClick={() => goToPage(totalPages)}
              disabled={currentPage >= totalPages}
              className={`px-3 py-1 border rounded ml-1 ${
                currentPage >= totalPages
                  ? "bg-gray-100 text-gray-400"
                  : "bg-white hover:bg-gray-100"
              }`}
            >
              Last
            </button>
          </div>
        </div>
      )}

      {/* ADD / EDIT MODAL */}
      {showForm && isAdmin && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">
              {selected ? "Edit Company" : "Add Company"}
            </h2>
            <form onSubmit={saveCompany} className="space-y-4">
              {[
                ["name", "Company Name *"],
                ["domain", "Domain *"],
                ["country", "Country"],
                ["industry", "Industry"],
                ["email_pattern", "Email Pattern"],
                ["size", "Size"],
              ].map(([k, ph]) => (
                <input
                  key={k}
                  name={k}
                  value={form[k]}
                  onChange={handleChange}
                  placeholder={ph}
                  required={ph.includes("*")}
                  className="w-full p-2 border rounded"
                />
              ))}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="number"
                  name="employeesCount"
                  value={form.employeesCount}
                  onChange={handleChange}
                  placeholder="Employees"
                  className="w-full p-2 border rounded"
                />
                <input
                  type="number"
                  name="revenueUsd"
                  value={form.revenueUsd}
                  onChange={handleChange}
                  placeholder="Revenue USD"
                  className="w-full p-2 border rounded"
                />
              </div>

              {["LNKD-URL", "LNKD-SalesURL"].map((k) => (
                <input
                  key={k}
                  name={k}
                  value={form[k]}
                  onChange={handleChange}
                  placeholder={k}
                  className="w-full p-2 border rounded"
                />
              ))}

              <textarea
                name="about"
                value={form.about}
                onChange={handleChange}
                placeholder="About"
                className="w-full p-2 border rounded"
                rows="3"
              />
              <textarea
                name="lnkd_company_summary"
                value={form.lnkd_company_summary}
                onChange={handleChange}
                placeholder="LinkedIn Summary"
                className="w-full p-2 border rounded"
                rows="4"
              />
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                placeholder="Notes"
                className="w-full p-2 border rounded"
                rows="3"
              />

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-300 rounded"
                  onClick={() => {
                    setShowForm(false);
                    setSelected(null);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  className="px-4 py-2 bg-purple-600 text-white rounded"
                >
                  {processing ? "Saving…" : selected ? "Update" : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* VIEW MODAL */}
      {showView && selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between mb-4">
              <h2 className="text-2xl font-bold">{selected.name}</h2>
              <button
                className="text-gray-500"
                onClick={() => setShowView(false)}
              >
                ✕
              </button>
            </div>

            <div className="company-details-container overflow-y-auto max-h-[60vh] pr-2">
              {[
                [
                  "Domain",
                  <a
                    href={`https://${selected.domain}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 underline flex items-center"
                  >
                    {selected.domain}
                    <FaExternalLinkAlt className="ml-1 text-xs" />
                  </a>,
                ],
                ["Country", selected.Country || "—"],
                ["Industry", selected.industry || "—"],
                ["Email Pattern", selected.email_pattern || "—"],
                ["Size", selected.size || "—"],
                [
                  "Employees",
                  selected.employeesCount
                    ? formatNumber(selected.employeesCount)
                    : "—",
                ],
                [
                  "Revenue",
                  selected.revenueUsd
                    ? `$${formatNumber(selected.revenueUsd)}`
                    : "—",
                ],
                [
                  "LinkedIn",
                  selected["LNKD-URL"] ? (
                    <a
                      href={selected["LNKD-URL"]}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 underline flex items-center"
                    >
                      Company Page
                      <FaExternalLinkAlt className="ml-1 text-xs" />
                    </a>
                  ) : (
                    "—"
                  ),
                ],
                [
                  "LinkedIn Sales",
                  selected["LNKD-SalesURL"] ? (
                    <a
                      href={selected["LNKD-SalesURL"]}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 underline flex items-center"
                    >
                      Sales Insights
                      <FaExternalLinkAlt className="ml-1 text-xs" />
                    </a>
                  ) : (
                    "—"
                  ),
                ],
              ].map(([label, val]) => (
                <p key={label}>
                  <span className="font-semibold">{label}:</span>{" "}
                  {typeof val === "string" ? val : val}
                </p>
              ))}

              {selected.about && (
                <div className="mt-4">
                  <h3 className="font-semibold">About</h3>
                  <p className="whitespace-pre-line">{selected.about}</p>
                </div>
              )}

              {selected.lnkd_company_summary && (
                <div className="mt-4">
                  <h3 className="font-semibold">LinkedIn Summary</h3>
                  <p className="whitespace-pre-line">
                    {selected.lnkd_company_summary}
                  </p>
                </div>
              )}

              {selected.notes && (
                <div className="mt-4">
                  <h3 className="font-semibold">Notes</h3>
                  <p className="whitespace-pre-line">{selected.notes}</p>
                </div>
              )}

              <p className="text-gray-500 mt-4 text-sm">
                Created:{" "}
                {selected.createdAt?.toDate
                  ? selected.createdAt.toDate().toLocaleString()
                  : new Date(selected.createdAt).toLocaleString()}
              </p>
            </div>

            {isAdmin && (
              <div className="flex justify-end mt-4">
                <button
                  className="px-4 py-2 bg-indigo-600 text-white rounded"
                  onClick={() => {
                    populateForm(selected);
                    setShowView(false);
                    setShowForm(true);
                  }}
                >
                  Edit
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Companies;
